import * as React from "react";
import { Alert, Snackbar, Stack } from "@mui/material";
import { Account, DeceasedRecord, Document, Person, ServiceProvider } from "@/types";
import { Hr } from "@/components/atoms/HR";
import { UpdateFormTemplateProc } from "@/templates/FormTemplate";
import { SPContext } from "@/store/SPProvider";
import { ExpressUiInfoContext } from "@/store/ExpressUiInfo";
import {
  ExpressAccountSummary,
  ExpressPersonSummary,
  SubmissionHeader,
} from "@/components/organisms/index";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { handleSubmit } from "./MultiAccountSubmit";
import { expressDisplayData } from "@/helpers/express-helpers";
import { DeceasedPersonalDetails } from "./DeceasedPersonalDetails";
import { useMatch, useNavigate } from "react-router-dom";
import { Terms } from "@/components/organisms/terms";

export type SubmitProps = {
  readonly serviceProvider: ServiceProvider;
  readonly onSectionClick: (section: string) => void;
  readonly updateTemplate: UpdateFormTemplateProc;
  uploadedFileInfo: (id: string) => Promise<Document>;
  next: () => void;
};
interface FetchState {
  busy: boolean;
  error?: string;
}
export interface ExpressSubmitPageUiData {
  accounts: Array<Account>;
  notifier: Person | undefined;
  nok: Person | undefined;
  executor: Person | undefined;
  deceased: DeceasedRecord | undefined;
}

export const Submit: React.FunctionComponent<SubmitProps> = ({
  serviceProvider,
  onSectionClick,
  updateTemplate,
  next,
}) => {
  useScrollToTop();

  const [{ accounts, notifier, nok, executor, deceased }, setLocalState] =
    React.useState<ExpressSubmitPageUiData>({
      accounts: [],
      notifier: undefined,
      nok: undefined,
      executor: undefined,
      deceased: undefined,
    });
  const { state: spState } = React.useContext(SPContext);
  const [{ busy, error }, setState] = React.useState<FetchState>({ busy: false, error: undefined });
  const { state: uiInfo } = React.useContext(ExpressUiInfoContext);

  React.useEffect(() => {
    setLocalState(expressDisplayData(spState));
  }, []);

  const onSubmit = React.useCallback(() => handleSubmit(spState, uiInfo, setState, next), [next]);

  React.useEffect(() => {
    updateTemplate({
      busy,
      currentSection: "submit",
      nextLabel: "Submit",
      saveLabel: null,
      onNext: onSubmit,
      onSectionClick,
    });
  }, [busy, onSubmit, onSectionClick, updateTemplate]);

  const match = useMatch("/express/:slug/*");
  const navigate = useNavigate();
  const onEditDeceased = React.useCallback(() => {
    navigate(`${match?.pathnameBase}/account`);
  }, [navigate, match]);

  return (
    <Stack rowGap={4}>
      <SubmissionHeader sp={serviceProvider} />

      <Hr />
      {deceased && <DeceasedPersonalDetails deceased={deceased} onEditDeceased={onEditDeceased} />}
      <Hr />

      {Array.isArray(accounts) &&
        accounts.length !== 0 &&
        accounts?.map((account: Account, index) => (
          <ExpressAccountSummary
            key={account?.id ?? index}
            sp={serviceProvider}
            account={account}
          />
        ))}

      <Hr />
      {notifier && (
        <>
          <ExpressPersonSummary person={notifier} />
          <Hr />
        </>
      )}
      {nok && (
        <>
          <ExpressPersonSummary person={nok} />
          <Hr />
        </>
      )}
      {executor && (
        <>
          <ExpressPersonSummary person={executor} />
          <Hr />
        </>
      )}
      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!error}
        autoHideDuration={6000}
        onClose={() => {}}
      >
        <Alert elevation={6} variant="filled" severity="error" onClose={() => {}}>
          {error}
        </Alert>
      </Snackbar>
      <Terms text="Submit" />
    </Stack>
  );
};
